import React, { useState, useEffect } from 'react';
import TeacherAssignmentsService from '../../services/TeacherAssignmentsService';
import ClassParserService from '../../services/ClassParserService';
import ClassTeachersView from './ClassTeachersView';
import CommitteeMembersView from './CommitteeMembersView';
import SubjectMembersView from './SubjectMembersView';
import SingleClassParser from './SingleClassParser';
import { classFilesData } from '../../Data/classFilesData';
import './TeacherAssignmentsManager.css';

const TeacherAssignmentsManager = ({ tenantId, onTeacherSelect }) => {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Parsing states
  const [parsingMode, setParsingMode] = useState(false);
  const [parsingProgress, setParsingProgress] = useState(0);
  const [parsingStats, setParsingStats] = useState(null);
  
  // Single class parser states
  const [showSingleClassParser, setShowSingleClassParser] = useState(false);
  const [showClassTeachersView, setShowClassTeachersView] = useState(false);
  const [showCommitteeMembersView, setShowCommitteeMembersView] = useState(false);
  const [showSubjectMembersView, setShowSubjectMembersView] = useState(false);

  // Form states for adding new assignments
  const [showAddAssignmentForm, setShowAddAssignmentForm] = useState(false);
  const [newAssignment, setNewAssignment] = useState({
    assignment_type: '',
    subject_name: '',
    class_id: '',
    committee_name: '',
    is_primary: false,
    teaching_level: '',
    responsibilities: ''
  });

  // Class view states
  const [showClassView, setShowClassView] = useState(false);

  // Load teachers with assignments on component mount
  useEffect(() => {
    loadTeachersWithAssignments();
  }, [tenantId]);

  const loadTeachersWithAssignments = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading teachers for tenantId:', tenantId);
      
      if (!tenantId) {
        throw new Error('No tenantId provided');
      }
      
      const teachersData = await TeacherAssignmentsService.getTeachersWithAssignments(tenantId);
      console.log('Loaded teachers:', teachersData.length, teachersData);
      
      // Ensure teachers are sorted by teacher_no in ascending order
      const sortedTeachers = teachersData.sort((a, b) => {
        const teacherNoA = a.teacher_no || 999999; // Put teachers without teacher_no at the end
        const teacherNoB = b.teacher_no || 999999;
        return teacherNoA - teacherNoB;
      });
      
      console.log('Sorted teachers by teacher_no:', sortedTeachers.map(t => ({ name: t.name, teacher_no: t.teacher_no })));
      setTeachers(sortedTeachers);
    } catch (err) {
      setError(`Failed to load teachers: ${err.message}`);
      console.error('Error loading teachers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTeacherClick = async (teacher) => {
    try {
      setLoading(true);
      const teacherWithAssignments = await TeacherAssignmentsService.getTeacherWithAssignments(tenantId, teacher.id);
      setSelectedTeacher(teacherWithAssignments);
      
      // Notify parent component
      if (onTeacherSelect) {
        onTeacherSelect(teacherWithAssignments);
      }
    } catch (err) {
      setError(`Failed to load teacher details: ${err.message}`);
      console.error('Error loading teacher details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleParseClassFiles = () => {
    setShowSingleClassParser(true);
  };

  const handleAssignmentsUpdated = async () => {
    // Reload teachers data after assignments are updated
    await loadTeachersWithAssignments();
    setMessage('Assignments updated successfully!');
    setMessageType('success');
  };


  const handleAddAssignment = async (e) => {
    e.preventDefault();
    
    if (!selectedTeacher) {
      setMessage('Please select a teacher first');
      setMessageType('error');
      return;
    }

    // Validate assignment data
    const assignmentData = {
      tenant_id: tenantId,
      teacher_id: selectedTeacher.id,
      ...newAssignment
    };

    const validation = TeacherAssignmentsService.validateAssignmentData(assignmentData);
    if (!validation.isValid) {
      setMessage(`Validation failed: ${validation.errors.join(', ')}`);
      setMessageType('error');
      return;
    }

    try {
      await TeacherAssignmentsService.addTeacherAssignment(assignmentData);
      setMessage('Assignment added successfully!');
      setMessageType('success');
      setNewAssignment({
        assignment_type: '',
        subject_name: '',
        class_id: '',
        committee_name: '',
        is_primary: false,
        teaching_level: '',
        responsibilities: ''
      });
      setShowAddAssignmentForm(false);
      
      // Reload teacher data
      await handleTeacherClick(selectedTeacher);
    } catch (err) {
      setMessage(`Failed to add assignment: ${err.message}`);
      setMessageType('error');
      console.error('Error adding assignment:', err);
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) {
      return;
    }

    try {
      await TeacherAssignmentsService.deleteTeacherAssignment(assignmentId);
      setMessage('Assignment deleted successfully!');
      setMessageType('success');
      
      // Reload teacher data
      if (selectedTeacher) {
        await handleTeacherClick(selectedTeacher);
      }
    } catch (err) {
      setMessage(`Failed to delete assignment: ${err.message}`);
      setMessageType('error');
      console.error('Error deleting assignment:', err);
    }
  };

  const getAssignmentTypeDisplay = (assignmentType) => {
    const typeMap = {
      'subject_teacher': 'Subject Teacher',
      'class_master': 'Class Master',
      'committee_member': 'Committee Member',
      'department_head': 'Department Head'
    };
    return typeMap[assignmentType] || assignmentType;
  };

  const getAssignmentTypeColor = (assignmentType) => {
    const colorMap = {
      'subject_teacher': '#667eea',
      'class_master': '#28a745',
      'committee_member': '#ffc107',
      'department_head': '#dc3545'
    };
    return colorMap[assignmentType] || '#6c757d';
  };


  if (loading && teachers.length === 0) {
    return (
      <div className="teacher-assignments-manager">
        <div className="loading">Loading teachers...</div>
      </div>
    );
  }

  // Show specific view if requested
  if (showClassTeachersView) {
    return (
      <ClassTeachersView 
        tenantId={tenantId} 
        onBack={() => setShowClassTeachersView(false)} 
      />
    );
  }

  if (showCommitteeMembersView) {
    return (
      <CommitteeMembersView 
        tenantId={tenantId} 
        onBack={() => setShowCommitteeMembersView(false)} 
      />
    );
  }

  if (showSubjectMembersView) {
    return (
      <SubjectMembersView 
        tenantId={tenantId} 
        onBack={() => setShowSubjectMembersView(false)} 
      />
    );
  }

  return (
    <div className="teacher-assignments-manager">
      <div className="manager-header">
        <h3>👥 Teacher Assignments Management</h3>
        <p>Manage teacher assignments, subjects taught, and class responsibilities.</p>
        
        <div className="view-mode-controls">
          <button
            className="view-mode-btn"
            onClick={() => setShowClassTeachersView(true)}
          >
            🏫 View by Classes
          </button>
          <button
            className="view-mode-btn"
            onClick={() => setShowCommitteeMembersView(true)}
          >
            👥 Committee Members
          </button>
          <button
            className="view-mode-btn"
            onClick={() => setShowSubjectMembersView(true)}
          >
            📚 Subject Members
          </button>
        </div>
        
        <div className="parsing-controls">
          <button
            className="parse-classes-btn"
            onClick={handleParseClassFiles}
          >
            📚 Parse Class Files
          </button>
          
          {parsingMode && (
            <div className="parsing-progress">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${parsingProgress}%` }}
                ></div>
              </div>
              <span>{parsingProgress}%</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {message && (
        <div className={`message ${messageType}`}>
          {messageType === 'success' ? '✅' : '❌'} {message}
        </div>
      )}

      {parsingStats && (
        <div className="parsing-stats">
          <h4>📊 Parsing Statistics</h4>
          <div className="stats-grid">
            <div className="stat-card">
              <h5>Total Assignments</h5>
              <span className="stat-number">{parsingStats.totalAssignments}</span>
            </div>
            <div className="stat-card">
              <h5>Classes Processed</h5>
              <span className="stat-number">{Object.keys(parsingStats.byClass).length}</span>
            </div>
            <div className="stat-card">
              <h5>Subjects</h5>
              <span className="stat-number">{Object.keys(parsingStats.bySubject).length}</span>
            </div>
            <div className="stat-card">
              <h5>Teachers</h5>
              <span className="stat-number">{Object.keys(parsingStats.byTeacher).length}</span>
            </div>
          </div>
        </div>
      )}

      <div className="manager-content">
        {/* Teachers List */}
        <div className="teachers-list">
          <h4>📋 Teachers ({teachers.length})</h4>
          <div className="teachers-grid">
            {teachers.map((teacher) => (
              <div
                key={teacher.id}
                className={`teacher-card ${selectedTeacher?.id === teacher.id ? 'selected' : ''}`}
                onClick={() => handleTeacherClick(teacher)}
              >
                <div className="teacher-info">
                  <h5>{teacher.name}</h5>
                  <p className="teacher-english-name">{teacher.english_name}</p>
                  <p className="teacher-department">{teacher.department}</p>
                  <p className="teacher-position">{teacher.position}</p>
                </div>
                <div className="teacher-assignments-summary">
                  <span className="assignment-count">{teacher.assignmentCount} assignments</span>
                  {teacher.assignments.slice(0, 2).map((assignment, index) => (
                    <span
                      key={index}
                      className="assignment-badge"
                      style={{ backgroundColor: getAssignmentTypeColor(assignment.assignment_type) }}
                    >
                      {getAssignmentTypeDisplay(assignment.assignment_type)}
                    </span>
                  ))}
                  {teacher.assignments.length > 2 && (
                    <span className="more-assignments">+{teacher.assignments.length - 2} more</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Selected Teacher Details */}
        {selectedTeacher && (
          <div className="teacher-details">
            <div className="teacher-header">
              <h4>👨‍🏫 {selectedTeacher.name}</h4>
              <p className="teacher-subtitle">{selectedTeacher.english_name} • {selectedTeacher.department}</p>
              <button
                className="add-assignment-btn"
                onClick={() => setShowAddAssignmentForm(!showAddAssignmentForm)}
              >
                ➕ Add Assignment
              </button>
            </div>

            {/* Add Assignment Form */}
            {showAddAssignmentForm && (
              <form className="add-assignment-form" onSubmit={handleAddAssignment}>
                <h5>Add New Assignment</h5>
                <div className="form-row">
                  <select
                    value={newAssignment.assignment_type}
                    onChange={(e) => setNewAssignment({ ...newAssignment, assignment_type: e.target.value })}
                    className="form-select"
                    required
                  >
                    <option value="">Select Assignment Type</option>
                    <option value="subject_teacher">Subject Teacher</option>
                    <option value="class_master">Class Master</option>
                    <option value="committee_member">Committee Member</option>
                    <option value="department_head">Department Head</option>
                  </select>
                </div>

                {newAssignment.assignment_type === 'subject_teacher' && (
                  <>
                    <div className="form-row">
                      <input
                        type="text"
                        placeholder="Subject Name"
                        value={newAssignment.subject_name}
                        onChange={(e) => setNewAssignment({ ...newAssignment, subject_name: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-row">
                      <input
                        type="text"
                        placeholder="Class ID (e.g., Class 1A)"
                        value={newAssignment.class_id}
                        onChange={(e) => setNewAssignment({ ...newAssignment, class_id: e.target.value })}
                        className="form-input"
                        required
                      />
                    </div>
                    <div className="form-row">
                      <select
                        value={newAssignment.teaching_level}
                        onChange={(e) => setNewAssignment({ ...newAssignment, teaching_level: e.target.value })}
                        className="form-select"
                      >
                        <option value="">Select Teaching Level</option>
                        <option value="Junior">Junior (F1-F3)</option>
                        <option value="Senior">Senior (F4-F6)</option>
                      </select>
                    </div>
                  </>
                )}

                {newAssignment.assignment_type === 'committee_member' && (
                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="Committee Name"
                      value={newAssignment.committee_name}
                      onChange={(e) => setNewAssignment({ ...newAssignment, committee_name: e.target.value })}
                      className="form-input"
                      required
                    />
                  </div>
                )}

                <div className="form-row">
                  <textarea
                    placeholder="Responsibilities"
                    value={newAssignment.responsibilities}
                    onChange={(e) => setNewAssignment({ ...newAssignment, responsibilities: e.target.value })}
                    className="form-textarea"
                    rows="3"
                  />
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={newAssignment.is_primary}
                      onChange={(e) => setNewAssignment({ ...newAssignment, is_primary: e.target.checked })}
                    />
                    Primary Assignment
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn">Add Assignment</button>
                  <button type="button" onClick={() => setShowAddAssignmentForm(false)} className="cancel-btn">
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Current Assignments */}
            <div className="current-assignments">
              <h5>Current Assignments ({selectedTeacher.assignments.length})</h5>
              {selectedTeacher.assignments.length === 0 ? (
                <p className="no-assignments">No assignments yet</p>
              ) : (
                <div className="assignments-list">
                  {selectedTeacher.assignments.map((assignment) => (
                    <div key={assignment.id} className="assignment-item">
                      <div className="assignment-info">
                        <span
                          className="assignment-type-badge"
                          style={{ backgroundColor: getAssignmentTypeColor(assignment.assignment_type) }}
                        >
                          {getAssignmentTypeDisplay(assignment.assignment_type)}
                        </span>
                        {assignment.subject_name && (
                          <span className="assignment-subject">Subject: {assignment.subject_name}</span>
                        )}
                        {assignment.class_id && (
                          <span className="assignment-class">Class: {assignment.class_id}</span>
                        )}
                        {assignment.committee_name && (
                          <span className="assignment-committee">Committee: {assignment.committee_name}</span>
                        )}
                        {assignment.teaching_level && (
                          <span className="assignment-level">Level: {assignment.teaching_level}</span>
                        )}
                        {assignment.is_primary && (
                          <span className="assignment-primary">Primary</span>
                        )}
                      </div>
                      <button
                        className="delete-assignment-btn"
                        onClick={() => handleDeleteAssignment(assignment.id)}
                        title="Delete Assignment"
                      >
                        🗑️
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Single Class Parser Modal */}
      {showSingleClassParser && (
        <SingleClassParser
          tenantId={tenantId}
          onClose={() => setShowSingleClassParser(false)}
          onAssignmentsUpdated={handleAssignmentsUpdated}
        />
      )}

    </div>
  );
};

export default TeacherAssignmentsManager;
